import type { FuseResultMatch } from "fuse.js";

/**
 * Highlights matched characters in text based on Fuse.js match indices
 */
export function highlightMatches(
  text: string,
  matches: readonly FuseResultMatch[] | undefined
): React.ReactNode {
  if (!matches || matches.length === 0) {
    return text;
  }

  // Get all match indices for this text
  const allIndices: [number, number][] = [];
  for (const match of matches) {
    if (match.indices) {
      allIndices.push(...match.indices);
    }
  }

  if (allIndices.length === 0) {
    return text;
  }

  // Sort and merge overlapping indices
  const sortedIndices = allIndices.sort((a, b) => a[0] - b[0]);
  const mergedIndices: [number, number][] = [sortedIndices[0]];

  for (let i = 1; i < sortedIndices.length; i++) {
    const current = sortedIndices[i];
    const last = mergedIndices[mergedIndices.length - 1];

    if (current[0] <= last[1] + 1) {
      // Overlapping or adjacent, merge them
      last[1] = Math.max(last[1], current[1]);
    } else {
      mergedIndices.push(current);
    }
  }

  // Build the highlighted text
  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const [start, end] of mergedIndices) {
    // Add non-highlighted text before this match
    if (start > lastIndex) {
      result.push(text.substring(lastIndex, start));
    }

    // Add highlighted text
    result.push(
      <mark
        key={`${start}-${end}`}
        style={{
          backgroundColor: "#ffd700",
          color: "inherit",
          fontWeight: 600,
          padding: 0,
        }}
      >
        {text.substring(start, end + 1)}
      </mark>
    );

    lastIndex = end + 1;
  }

  // Add remaining non-highlighted text
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }

  return result;
}
